import React, { useEffect, useState } from 'react'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts'
import { LineChart } from '@mantine/charts'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store'
import { apiRequest } from '../../utils/api'
import styles from './ManagerDashboardBox.module.scss'

const LEVEL_COLORS = [
  '#425AA8',
  '#5B8FF9',
  '#5AD8A6',
  '#F6BD16',
  '#E8684A',
  '#6DC8EC',
  '#9270CA',
  '#FF9D4D',
  '#269A99',
]

const ICONS = {
  students: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  ),
  accuracy: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-12.5c-2.49 0-4.5 2.01-4.5 4.5S9.51 16.5 12 16.5s4.5-2.01 4.5-4.5S14.49 7.5 12 7.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5S10.62 9.5 12 9.5s2.5 1.12 2.5 2.5S13.38 16.5 12 16.5zm0-3.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/>
    </svg>
  ),
  inactive: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
    </svg>
  ),
  wrongLevel: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zM16.2 13h2.8v6h-2.8v-6z"/>
    </svg>
  ),
}

const KPI_CARDS = (data) => [
  {
    icon: ICONS.students,
    colorClass: 'blue',
    label: 'Total Students',
    value: data.TOTAL_STUDENTS,
    sub: 'Active students',
  },
  {
    icon: ICONS.accuracy,
    colorClass: 'green',
    label: 'Avg. Accuracy',
    value: data.AVG_ACCURACY != null ? `${data.AVG_ACCURACY}%` : '-',
    sub: 'All students average',
  },
  {
    icon: ICONS.inactive,
    colorClass: 'orange',
    label: 'Inactive 7+ Days',
    value: data.INACTIVE_7DAYS,
    sub: 'No activity in 7 days',
  },
  {
    icon: ICONS.wrongLevel,
    colorClass: 'red',
    label: 'Most Missed Level',
    value: data.MOST_WRONG_LEVEL_NO || '-',
    sub: 'By cumulative wrong answers',
  },
]

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className={styles.tooltip}>
      <p className={styles.tooltipLabel}>{label}</p>
      <p className={styles.tooltipValue}>{payload[0].value} students</p>
    </div>
  )
}

const ManagerDashboardBox = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)

  const [summaryData, setSummaryData] = useState({
    TOTAL_STUDENTS: 0,
    MOST_WRONG_LEVEL_NO: '-',
    AVG_ACCURACY: null,
    INACTIVE_7DAYS: 0,
  })
  const [levelData, setLevelData] = useState([])
  const [averageData, setAverageData] = useState([])
  const [recentRegistrations, setRecentRegistrations] = useState([])

  useEffect(() => {
    if (!user?.CENTER_ID) {
      console.warn('[Dashboard] CENTER_ID가 없습니다. user:', user)
      return
    }
    fetchSummary(user.CENTER_ID)
    fetchChartData(user.CENTER_ID)
  }, [user?.CENTER_ID])

  const fetchSummary = async (CENTER) => {
    try {
      const res = await apiRequest('/fetchManagerDashBoard', 'POST', { CENTER })
      if (res.data) setSummaryData(res.data)
      else console.warn('[Dashboard] fetchManagerDashBoard 응답 없음', res)
    } catch (e) {
      console.error('[Dashboard] fetchManagerDashBoard 실패:', e)
    }
  }

  const fetchChartData = async (CENTER) => {
    try {
      const res = await apiRequest('/fetchLevelStudent2', 'POST', { CENTER })
      if (res.data) {
        setLevelData(res.data.levels)
        setRecentRegistrations(res.data.recentRegistrations)
        setAverageData(res.data.averageData)
      } else {
        console.warn('[Dashboard] fetchLevelStudent2 응답 없음', res)
      }
    } catch (e) {
      console.error('[Dashboard] fetchLevelStudent2 실패:', e)
    }
  }

  const getInitial = (name) => (name ? name[0].toUpperCase() : '?')

  const handleBarClick = (data) => {
    const selectedLevel = data?.activePayload?.[0]?.payload?.level?.replace('Level ', '')
    if (selectedLevel) navigate('/studentInfo', { state: { level: selectedLevel } })
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2>Overview</h2>
        <p>Welcome back! Here's what's happening with your center.</p>
      </div>

      {/* KPI Cards */}
      <div className={styles.kpiRow}>
        {KPI_CARDS(summaryData).map((card) => (
          <div key={card.label} className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles[card.colorClass]}`}>
              {card.icon}
            </div>
            <div className={styles.kpiBody}>
              <span className={styles.kpiLabel}>{card.label}</span>
              <span className={styles.kpiValue}>{card.value}</span>
              <span className={styles.kpiSub}>{card.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className={styles.mainGrid}>
        {/* Left: Charts */}
        <div className={styles.chartsCol}>
          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Student Distribution by Level</h3>
              <span className={styles.chartNote}>Click a bar to view students</span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <ReBarChart
                data={levelData}
                margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                onClick={handleBarClick}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="level"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="students" radius={[6, 6, 0, 0]} maxBarSize={48}>
                  {levelData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={LEVEL_COLORS[index % LEVEL_COLORS.length]}
                    />
                  ))}
                </Bar>
              </ReBarChart>
            </ResponsiveContainer>

            {/* Color legend */}
            {levelData.length > 0 && (
              <div className={styles.legend}>
                {levelData.map((item, index) => (
                  <div key={item.level} className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: LEVEL_COLORS[index % LEVEL_COLORS.length] }}
                    />
                    <span className={styles.legendLabel}>{item.level}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Monthly Registration Status</h3>
            </div>
            <LineChart
              h={220}
              data={averageData}
              dataKey="MONTH"
              series={[{ name: 'student', color: '#425AA8', label: 'New Students' }]}
              curveType="monotone"
              gridAxis="xy"
            />
          </div>
        </div>

        {/* Right: Recently Registered */}
        <div className={styles.sideCol}>
          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Recently Registered</h3>
              <button
                className={styles.viewAllBtn}
                onClick={() => navigate('/admin/users')}
              >
                View All
              </button>
            </div>
            {recentRegistrations.length === 0 ? (
              <p className={styles.listEmpty}>No recent registrations.</p>
            ) : (
              recentRegistrations.map((item, i) => (
                <div
                  key={i}
                  className={styles.listItem}
                  onClick={() => navigate('/admin/users')}
                >
                  <div className={styles.listLeft}>
                    <div className={styles.listAvatar}>{getInitial(item.name)}</div>
                    <span className={styles.listName}>{item.name}</span>
                  </div>
                  <span className={styles.listDate}>{item.date}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ManagerDashboardBox
